import { z } from "zod";
import { requiredAddressSchema } from "../organization/address";
import { optionalPhoneBRSchema } from "../common/phone";
import { cpfSchema, optionalCpfSchema } from "../common/document";
import { brazilianStateSchema } from "./cro";
import { organizationTypeSchema } from "../organization/types";

/** Update parcial do proprio perfil — todos campos opcionais. */
export const updateProfileSchema = z.object({
  name: z.string().trim().min(2, "Nome muito curto").max(120).optional(),
  phone: optionalPhoneBRSchema,
  cpf: optionalCpfSchema,
  croNumber: z
    .string()
    .trim()
    .regex(/^\d{3,8}$/, "Numero CRO invalido")
    .optional()
    .or(z.literal("").transform(() => undefined)),
  croState: brazilianStateSchema.optional(),
  /** Biografia profissional; texto vazio apaga. */
  bio: z.string().trim().max(500, "Biografia com no máximo 500 caracteres").optional(),
});

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/** Preenchimento inicial obrigatório do perfil — nome, CPF e CRO obrigatórios (ver issue #5),
 *  área de atuação obrigatória, resto opcional. */
export const completeProfileSchema = z.object({
  name: z.string().trim().min(2, "Nome muito curto").max(120),
  phone: optionalPhoneBRSchema,
  cpf: cpfSchema,
  croNumber: z.string().trim().regex(/^\d{3,8}$/, "Numero CRO invalido"),
  croState: brazilianStateSchema,
  types: z.array(organizationTypeSchema).length(1, "Selecione uma área"),
  /** Endereço da organização pessoal — obrigatório: é pra onde a prótese volta (clínica) e de onde
   *  ela sai (laboratório), e vai nas etiquetas. */
  address: requiredAddressSchema,
  /** Especialidades odontológicas escolhidas — só relevante quando types inclui DENTAL_OFFICE.
   *  Vira tag pessoal (UserSpecialty) e especialidade ativa da organização pessoal (OrganizationSpecialty).
   *  Os procedimentos vêm da base de mercado (Configurações › Especialidades e Serviços). */
  specialtyIds: z.array(z.string()).default([]),
  /** Token de convite de parceria (?connectInvite=partner:TOKEN) guardado no navegador durante o
   *  cadastro. Quando a organização pessoal criada é clínica, o aceite acontece aqui mesmo — quem
   *  chegou pelo link do laboratório sai do onboarding já conectado, sem passo extra. */
  connectInviteToken: z.string().min(1).optional(),
});

export type CompleteProfileInput = z.infer<typeof completeProfileSchema>;

/** Substitui o conjunto de especialidades pessoais do usuário (tag, não mexe no catálogo da organização). */
export const updateSpecialtiesSchema = z.object({
  specialtyIds: z.array(z.string()),
});

export type UpdateSpecialtiesInput = z.infer<typeof updateSpecialtiesSchema>;

/** Adiciona um e-mail adicional (precisa verificar antes de servir pra reivindicar convites). */
export const addEmailSchema = z.object({
  email: z.string().trim().toLowerCase().email("E-mail inválido"),
});

export type AddEmailInput = z.infer<typeof addEmailSchema>;

/** Confirma um e-mail adicional a partir do token recebido por e-mail. */
export const verifyEmailSchema = z.object({
  token: z.string().min(1),
});

export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

/** Remove um e-mail adicional do próprio usuário. */
export const removeEmailSchema = z.object({
  id: z.string().min(1),
});

export type RemoveEmailInput = z.infer<typeof removeEmailSchema>;
